
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  onUnmounted,
  watch,
  Ref
} from "vue";
import { useRouter } from "vue-router";
import Checkbox from "primevue/checkbox";
import RntrcSolicitacaoDetalhe from "./RntrcSolicitacaoDetalhe.vue";
import RntrcPixBloco from "./RntrcPixBloco.vue";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";
import { PaginationResult, defaultResults } from "../../pagination";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { DATE_SELECTORS, formatDateTime } from "../../modules/utils";
import { useConfirm, useToast } from "../../modules/message";
import { MenuModel, MenuEvent } from "../../router";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import {
  useRntrcSolicitacaoService,
  podeNaSolicitacao,
  placasAtivas,
  pixEmAberto,
  tipoNovoCadastro
} from "../../modules/business/rntrc/rntrcSolicitacaoBusiness";
import {
  DocumentoRntrc,
  RntrcSolicitacaoFilterPayload,
  RntrcSolicitacaoPayload,
  SITUACOES_RNTRC,
  TIPOS_SERVICO_RNTRC,
  formatCpfCnpj,
  formatMoeda,
  formatPlaca,
  formatTempoRestante,
  getDocumentoLabel,
  getSituacaoCor,
  getSituacaoLabel,
  getTipoServicoLabel,
  isEmAndamento,
  normalizarPlaca,
  somenteDigitos,
  tipoComVeiculos
} from "../../modules/business/rntrc/rntrcTypes";

const INTERVALO_REFRESH_MS = 30 * 1000;

interface RntrcSolicitacaoListState {
  stateReady: boolean;
  filter: RntrcSolicitacaoFilterPayload;
  numeroSolicitacao: string;
  tipoPeriodo: string;
  page: number;
  results: PaginationResult<RntrcSolicitacaoPayload>;
  loadings: Array<Ref<boolean>>;
  idDetalhe: number | null;
  versaoDetalhe: number;
  agora: Date;
  intervalRefresh?: number | null;
  intervalRelogio?: number | null;
  pix: {
    aberto: boolean;
    entity: RntrcSolicitacaoPayload | null;
  };
  reenvio: {
    aberto: boolean;
    enviando: boolean;
    entity: RntrcSolicitacaoPayload | null;
    flProsseguirParcial: boolean;
  };
  cancelamentoPlacas: {
    aberto: boolean;
    enviando: boolean;
    entity: RntrcSolicitacaoPayload | null;
    selecionadas: Array<number>;
  };
}

export default defineComponent({
  setup(props) {
    const router = useRouter();

    const state = reactive<RntrcSolicitacaoListState>({
      stateReady: false,
      filter: useRntrcSolicitacaoService().safeFilter(),
      numeroSolicitacao: "",
      tipoPeriodo: DATE_SELECTORS.LAST_MONTH,
      page: 0,
      results: defaultResults(),
      loadings: [],
      idDetalhe: null,
      versaoDetalhe: 0,
      agora: new Date(),
      intervalRefresh: null,
      intervalRelogio: null,
      pix: { aberto: false, entity: null },
      reenvio: {
        aberto: false,
        enviando: false,
        entity: null,
        flProsseguirParcial: false
      },
      cancelamentoPlacas: {
        aberto: false,
        enviando: false,
        entity: null,
        selecionadas: []
      }
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const stateLoading = computed(() => state.loadings.length !== 0);

    const situacoesDisponiveis = SITUACOES_RNTRC;
    const tiposDisponiveis = TIPOS_SERVICO_RNTRC;

    const autoRefreshAtivo = computed(() =>
      state.results.content.some((s) => isEmAndamento(s.flSituacao))
    );

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const executeSearch = (silencioso?: boolean) => {
      const { data, loading, listar } = useRntrcSolicitacaoService();
      state.filter.idRntrcSolicitacao = state.numeroSolicitacao
        ? +somenteDigitos(state.numeroSolicitacao)
        : null;
      if (!silencioso) {
        state.loadings.push(loading);
      }
      const filtro = {
        ...state.filter,
        cpfCnpj: somenteDigitos(state.filter.cpfCnpj) || null,
        placa: normalizarPlaca(state.filter.placa) || null
      };
      return listar(filtro, state.page, silencioso)
        .then(() => {
          if (data.value && data.value.content) {
            state.results = data.value;
          } else {
            state.results = defaultResults();
          }
          state.page = state.results.page.number;
          removeLoading(loading);
        })
        .catch(() => {
          removeLoading(loading);
        });
    };

    const submitSearch = () => {
      state.page = 0;
      state.results = defaultResults();
      return executeSearch();
    };

    const afterPageChange = (page?: number) => {
      if (typeof page !== "undefined") {
        state.page = page;
      }
      executeSearch();
    };

    const clearFilter = () => {
      state.filter = useRntrcSolicitacaoService().safeFilter();
      state.numeroSolicitacao = "";
      state.tipoPeriodo = DATE_SELECTORS.LAST_MONTH;
      state.results = defaultResults();
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : null;
    };

    const emPagamento = (s: RntrcSolicitacaoPayload) =>
      s.flSituacao === "PAG" || s.flSituacao === "PGI";

    const vencido = (s: RntrcSolicitacaoPayload) =>
      !!s.dataVencimentoPix &&
      s.dataVencimentoPix.getTime() <= state.agora.getTime();

    const resumoPlacas = (s: RntrcSolicitacaoPayload) => {
      const placas = (s.placas ?? "")
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p);
      const qtd = s.quantidadeVeiculos ?? placas.length;
      if (placas.length && placas.length <= 2 && qtd <= 2) {
        return placas.map((p) => formatPlaca(p)).join(", ");
      }
      return qtd + " placa" + (qtd !== 1 ? "s" : "");
    };

    // ---------------------------------------------------------------------
    // Detalhe e PIX
    // ---------------------------------------------------------------------

    const abrirDetalhe = (idRntrcSolicitacao?: number | null) => {
      if (idRntrcSolicitacao) {
        state.idDetalhe = idRntrcSolicitacao;
        state.versaoDetalhe++;
      }
    };

    const fecharDetalhe = (idFechado?: number) => {
      if (idFechado && idFechado !== state.idDetalhe) {
        return; // outro detalhe já foi aberto no lugar
      }
      state.idDetalhe = null;
      if (router.currentRoute.value.name === "rntrc-solicitacao-detalhe") {
        router.replace({ name: "rntrc-solicitacao" });
      }
    };

    const recarregarDetalhe = () => {
      if (state.idDetalhe) {
        state.versaoDetalhe++;
      }
    };

    const abrirPix = (s: RntrcSolicitacaoPayload) => {
      if (!s.idRntrcSolicitacao) {
        return;
      }
      state.pix.entity = null;
      state.pix.aberto = true;
      const { data, buscar } = useRntrcSolicitacaoService();
      buscar(s.idRntrcSolicitacao)
        .then(() => {
          state.pix.entity = data.value ?? null;
        })
        .catch(() => {
          state.pix.aberto = false;
        });
    };

    // ---------------------------------------------------------------------
    // Ações
    // ---------------------------------------------------------------------

    const aposAcao = (mensagem: string) => {
      useToast().success(mensagem);
      executeSearch();
      recarregarDetalhe();
    };

    const confirmarPagamento = (s?: RntrcSolicitacaoPayload | null) => {
      if (!s?.idRntrcSolicitacao) {
        return;
      }
      const idRntrcSolicitacao = s.idRntrcSolicitacao;
      useConfirm().require({
        message:
          "Confirma que o transportador pagou o PIX da solicitação nº " +
          idRntrcSolicitacao +
          "? A verificação na ANTT é antecipada; o PIX pode levar até 1 dia útil para virar saldo.",
        header: "Confirmar pagamento",
        icon: "pi pi-info-circle",
        accept: () => {
          const { loading, confirmarPagamento } = useRntrcSolicitacaoService();
          state.loadings.push(loading);
          confirmarPagamento(idRntrcSolicitacao)
            .then(() => {
              removeLoading(loading);
              state.pix.aberto = false;
              aposAcao("Pagamento informado. A verificação foi antecipada.");
            })
            .catch(() => removeLoading(loading));
        }
      });
    };

    const abrirReenvio = (s: RntrcSolicitacaoPayload) => {
      state.reenvio.entity = s;
      state.reenvio.flProsseguirParcial = !!s.flProsseguirParcial;
      state.reenvio.enviando = false;
      state.reenvio.aberto = true;
    };

    const reenvioComPlacas = computed(() =>
      tipoComVeiculos(state.reenvio.entity?.flTipoServico)
    );

    const confirmarReenvio = () => {
      const id = state.reenvio.entity?.idRntrcSolicitacao;
      if (!id) {
        return;
      }
      const { reenviar } = useRntrcSolicitacaoService();
      state.reenvio.enviando = true;
      reenviar(id, state.reenvio.flProsseguirParcial)
        .then(() => {
          state.reenvio.aberto = false;
          aposAcao("Solicitação nº " + id + " reenviada para o robô.");
        })
        .finally(() => {
          state.reenvio.enviando = false;
        });
    };

    const cancelar = (s: RntrcSolicitacaoPayload) => {
      const id = s.idRntrcSolicitacao;
      if (!id) {
        return;
      }
      useConfirm().require({
        message:
          "Deseja cancelar a solicitação nº " +
          id +
          "? O cancelamento é definitivo; para tentar de novo, crie outra solicitação.",
        header: "Cancelar solicitação",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        accept: () => {
          const { loading, cancelar } = useRntrcSolicitacaoService();
          state.loadings.push(loading);
          cancelar(id)
            .then(() => {
              removeLoading(loading);
              aposAcao("Solicitação nº " + id + " cancelada.");
            })
            .catch(() => removeLoading(loading));
        }
      });
    };

    // Cancelamento na ANTT antes de pagar: quem executa no SITCarga é o robô.
    const enviarCancelamentoNaAntt = (
      id: number,
      idsVeiculos: Array<number>
    ) => {
      const { cancelarNaAntt } = useRntrcSolicitacaoService();
      state.cancelamentoPlacas.enviando = true;
      return cancelarNaAntt(id, idsVeiculos)
        .then(() => {
          state.pix.aberto = false;
          state.cancelamentoPlacas.aberto = false;
          aposAcao(
            "Cancelamento da solicitação nº " +
              id +
              " enviado. O robô vai cancelar na ANTT."
          );
        })
        .finally(() => {
          state.cancelamentoPlacas.enviando = false;
        });
    };

    const cancelarPedidoNaAntt = (s?: RntrcSolicitacaoPayload | null) => {
      const id = s?.idRntrcSolicitacao;
      if (!id) {
        return;
      }
      useConfirm().require({
        message:
          "O robô vai cancelar na ANTT o pedido " +
          (s?.idPedidoSitcarga ?? "") +
          " da solicitação nº " +
          id +
          ", com todas as placas. O PIX atual deixa de valer: não pague. A solicitação fica cancelada e o serviço não é cobrado.",
        header: "Cancelar pedido na ANTT",
        icon: "pi pi-exclamation-triangle",
        acceptClass: "p-button-danger",
        accept: () => {
          enviarCancelamentoNaAntt(id, []);
        }
      });
    };

    const pixAberto = computed(() => pixEmAberto(state.pix.entity));

    // Placa presa em contrato de outro transportador: a exclusão é feita por baixo, na mesma
    // solicitação, e a inclusão segue sozinha depois do pagamento.
    const excluirFrotaAtual = (s?: RntrcSolicitacaoPayload | null) => {
      const id = s?.idRntrcSolicitacao;
      if (!id) {
        return;
      }
      useConfirm().require({
        message:
          (s?.msgCliente ? s.msgCliente + " " : "") +
          "A GovPass faz primeiro a exclusão da frota atual (cobrada como uma exclusão) e, assim que o PIX dela for pago, segue com esta inclusão automaticamente. Prosseguir?",
        header: "Exclusão da frota atual",
        icon: "pi pi-exclamation-triangle",
        accept: () => {
          const { loading, excluirFrotaAtual } = useRntrcSolicitacaoService();
          state.loadings.push(loading);
          excluirFrotaAtual(id)
            .then(() => {
              removeLoading(loading);
              aposAcao(
                "Exclusão da frota atual iniciada na solicitação nº " + id + "."
              );
            })
            .catch(() => removeLoading(loading));
        }
      });
    };

    const abrirCancelamentoPlacas = (s?: RntrcSolicitacaoPayload | null) => {
      if (!s?.idRntrcSolicitacao) {
        return;
      }
      state.cancelamentoPlacas.entity = s.veiculos ? s : null;
      state.cancelamentoPlacas.selecionadas = [];
      state.cancelamentoPlacas.enviando = false;
      state.cancelamentoPlacas.aberto = true;
      if (!s.veiculos) {
        // a lista não traz os veículos
        const { data, buscar } = useRntrcSolicitacaoService();
        buscar(s.idRntrcSolicitacao)
          .then(() => {
            state.cancelamentoPlacas.entity = data.value ?? null;
          })
          .catch(() => {
            state.cancelamentoPlacas.aberto = false;
          });
      }
    };

    const placasAtivasDoCancelamento = computed(() =>
      placasAtivas(state.cancelamentoPlacas.entity)
    );

    const todasPlacasSelecionadas = computed(
      () =>
        placasAtivasDoCancelamento.value.length > 0 &&
        state.cancelamentoPlacas.selecionadas.length ===
          placasAtivasDoCancelamento.value.length
    );

    const confirmarCancelamentoPlacas = () => {
      const id = state.cancelamentoPlacas.entity?.idRntrcSolicitacao;
      if (!id || !state.cancelamentoPlacas.selecionadas.length) {
        return;
      }
      enviarCancelamentoNaAntt(id, [...state.cancelamentoPlacas.selecionadas]);
    };

    const editar = (s: RntrcSolicitacaoPayload) => {
      if (s.idRntrcSolicitacao) {
        router.push({
          name: "rntrc-solicitacao-edit",
          params: { idRntrcSolicitacao: s.idRntrcSolicitacao + "" }
        });
      }
    };

    const incluirNoDestino = (s: RntrcSolicitacaoPayload) => {
      if (s.idRntrcSolicitacao) {
        router.push({
          name: "rntrc-solicitacao-new",
          query: { tipo: "IMF", origem: s.idRntrcSolicitacao + "" }
        });
      }
    };

    /** Consulta "sem cadastro": abre o novo cadastro (TAC ou ETC) com o mesmo transportador. */
    const fazerCadastro = (s: RntrcSolicitacaoPayload) => {
      if (s.idRntrcSolicitacao) {
        router.push({
          name: "rntrc-solicitacao-new",
          query: {
            tipo: tipoNovoCadastro(s.cpfCnpj),
            origem: s.idRntrcSolicitacao + ""
          }
        });
      }
    };

    const novaSolicitacao = () => {
      router.push({ name: "rntrc-solicitacao-new" });
    };

    const abrirDocumento = (
      s: RntrcSolicitacaoPayload,
      tipo: DocumentoRntrc,
      newTab?: boolean
    ) => {
      if (s.idRntrcSolicitacao) {
        const { loading, abrirDocumento } = useRntrcSolicitacaoService();
        state.loadings.push(loading);
        abrirDocumento(s.idRntrcSolicitacao, tipo, newTab)
          .then(() => removeLoading(loading))
          .catch(() => removeLoading(loading));
      }
    };

    const executarAcao = (acao: string, s?: RntrcSolicitacaoPayload | null) => {
      if (!s) {
        return;
      }
      switch (acao) {
        case "confirmar-pagamento":
          confirmarPagamento(s);
          break;
        case "reenviar":
          abrirReenvio(s);
          break;
        case "cancelar":
          cancelar(s);
          break;
        case "cancelar-pedido-antt":
          cancelarPedidoNaAntt(s);
          break;
        case "cancelar-placas-antt":
          abrirCancelamentoPlacas(s);
          break;
        case "excluir-frota-atual":
          excluirFrotaAtual(s);
          break;
        case "editar":
          editar(s);
          break;
        case "incluir-destino":
          incluirNoDestino(s);
          break;
        case "fazer-cadastro":
          fazerCadastro(s);
          break;
      }
    };

    const menuAcoes = (s: RntrcSolicitacaoPayload) => {
      const items = [] as Array<MenuModel>;
      items.push({
        label: "Detalhes",
        icon: "fas fa-search",
        command: () => abrirDetalhe(s.idRntrcSolicitacao)
      });
      if (podeNaSolicitacao("excluir-frota-atual", s)) {
        items.push({
          label: "Prosseguir com a exclusão",
          icon: "fas fa-exchange-alt",
          command: () => excluirFrotaAtual(s)
        });
      }
      if (emPagamento(s) || s.flSituacao === "AEX") {
        items.push({
          label: "PIX",
          icon: "fas fa-qrcode",
          command: () => abrirPix(s)
        });
      }
      if (podeNaSolicitacao("confirmar-pagamento", s)) {
        items.push({
          label: "Confirmar pagamento",
          icon: "fas fa-check",
          command: () => confirmarPagamento(s)
        });
      }
      if (podeNaSolicitacao("cancelar-placas-antt", s)) {
        items.push({
          label: "Cancelar placas",
          icon: "fas fa-minus-circle",
          command: () => abrirCancelamentoPlacas(s)
        });
      }
      if (podeNaSolicitacao("cancelar-pedido-antt", s)) {
        items.push({
          label: "Cancelar pedido na ANTT",
          icon: "fas fa-ban",
          command: () => cancelarPedidoNaAntt(s)
        });
      }
      if (podeNaSolicitacao("reenviar", s)) {
        items.push({
          label: "Reenviar",
          icon: "fas fa-redo",
          command: () => abrirReenvio(s)
        });
      }
      if (podeNaSolicitacao("cancelar", s)) {
        items.push({
          label: "Cancelar",
          icon: "fas fa-ban",
          command: () => cancelar(s)
        });
      }
      if (podeNaSolicitacao("editar", s)) {
        items.push({
          label: "Editar",
          icon: "pi pi-pencil",
          command: () => editar(s)
        });
      }
      if (podeNaSolicitacao("incluir-destino", s)) {
        items.push({
          label: "Incluir no destino",
          icon: "fas fa-exchange-alt",
          command: () => incluirNoDestino(s)
        });
      }
      if (podeNaSolicitacao("fazer-cadastro", s)) {
        items.push({
          label: "Fazer cadastro na ANTT",
          icon: "fas fa-user-plus",
          command: () => fazerCadastro(s)
        });
      }
      if (podeNaSolicitacao("download", s)) {
        const docs: Array<{
          possui?: boolean | null;
          tipo: DocumentoRntrc;
        }> = [
          { possui: s.possuiExtrato, tipo: "EXT" },
          { possui: s.possuiCertificado, tipo: "CER" },
          { possui: s.possuiRelatorio, tipo: "REL" },
          { possui: s.possuiComprovante, tipo: "COM" }
        ];
        docs
          .filter((d) => !!d.possui)
          .map((d) => d.tipo)
          .forEach((tipo) => {
            items.push({
              label: getDocumentoLabel(tipo),
              icon: "far fa-file-pdf",
              command: (event?: MenuEvent) => {
                if (event?.originalEvent) {
                  event.originalEvent.preventDefault();
                }
                abrirDocumento(s, tipo, event?.originalEvent?.button == 1);
              }
            });
          });
      }
      return items;
    };

    // ---------------------------------------------------------------------
    // Auto-refresh (30 s enquanto houver linha não terminal) e relógio do PIX
    // ---------------------------------------------------------------------

    const iniciarTimers = () => {
      state.intervalRefresh = window.setInterval(() => {
        if (autoRefreshAtivo.value && !stateLoading.value) {
          executeSearch(true);
        }
      }, INTERVALO_REFRESH_MS);
      state.intervalRelogio = window.setInterval(() => {
        state.agora = new Date();
      }, INTERVALO_REFRESH_MS);
    };

    const pararTimers = () => {
      if (state.intervalRefresh) {
        clearInterval(state.intervalRefresh);
        state.intervalRefresh = null;
      }
      if (state.intervalRelogio) {
        clearInterval(state.intervalRelogio);
        state.intervalRelogio = null;
      }
    };

    watch(
      () => props.idRntrcSolicitacao,
      (id) => {
        if (id) {
          abrirDetalhe(id);
        }
      }
    );

    onMounted(() => {
      state.stateReady = true;
      executeSearch();
      if (props.idRntrcSolicitacao) {
        abrirDetalhe(props.idRntrcSolicitacao);
      }
      iniciarTimers();
    });

    onUnmounted(() => {
      pararTimers();
    });

    return {
      amIAllowed,
      podeNaSolicitacao,
      stateLoading,
      situacoesDisponiveis,
      tiposDisponiveis,
      autoRefreshAtivo,
      submitSearch,
      afterPageChange,
      clearFilter,
      confirmSelectionCliente,
      emPagamento,
      vencido,
      resumoPlacas,
      abrirDetalhe,
      fecharDetalhe,
      abrirPix,
      confirmarPagamento,
      cancelarPedidoNaAntt,
      excluirFrotaAtual,
      pixAberto,
      abrirCancelamentoPlacas,
      placasAtivasDoCancelamento,
      todasPlacasSelecionadas,
      confirmarCancelamentoPlacas,
      reenvioComPlacas,
      confirmarReenvio,
      executarAcao,
      menuAcoes,
      novaSolicitacao,
      normalizarPlaca,
      formatDateTime,
      formatCpfCnpj,
      formatMoeda,
      formatPlaca,
      formatTempoRestante,
      getSituacaoCor,
      getSituacaoLabel,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  },
  components: {
    Checkbox,
    RntrcSolicitacaoDetalhe,
    RntrcPixBloco,
    EntitySelectorCliente
  },
  props: {
    idRntrcSolicitacao: Number
  }
});
